<template>
  <div class="referrals-settings">
    <vx-card title="Referral program">
      <div v-if="loadingSettings" class="text-center py-10">
        <div class="w-8 h-8 pt_loading inline-block"></div>
        <p class="mt-2 text-light">Loading settings...</p>
      </div>

      <div v-else>
        <p class="mb-4 text-sm" style="color:#888">
          Reward happy customers for sending friends your way. Friends get a welcome reward
          when they're invited; you only reward the referrer once you've confirmed their
          friend is a genuine new customer.
        </p>

        <div class="flex items-center mb-4">
          <vs-switch v-model="settings.enabled" class="mr-3" />
          <span>Referral program is <strong>{{ settings.enabled ? 'ON' : 'OFF' }}</strong></span>
        </div>

        <div class="mb-5">
          <label class="vs-input--label mb-2">How would you like rewards to work?</label>
          <div class="reward-mode-option">
            <vs-radio vs-name="rewardmode" v-model="rewardMode" vs-value="noreward">
              No rewards
              <span class="text-sm" style="color:#999"> — friends who sign up simply appear in your referral list</span>
            </vs-radio>
          </div>
          <div class="reward-mode-option">
            <vs-radio vs-name="rewardmode" v-model="rewardMode" vs-value="onceoff">
              Thank the referrer once
              <span class="text-sm" style="color:#999"> — only their first successful referral earns them a thank-you</span>
            </vs-radio>
          </div>
          <div class="reward-mode-option">
            <vs-radio vs-name="rewardmode" v-model="rewardMode" vs-value="everyfriend">
              Thank the referrer for every friend
              <span class="text-sm" style="color:#999"> — a new thank-you each time a friend becomes a customer</span>
            </vs-radio>
          </div>
          <div class="reward-mode-option">
            <vs-radio vs-name="rewardmode" v-model="rewardMode" vs-value="group">
              Group reward
              <span class="text-sm" style="color:#999"> — friends team up, and the whole group unlocks one shared reward</span>
            </vs-radio>
          </div>
        </div>

        <template v-if="rewardMode === 'group'">
          <div class="vx-row">
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="vs-input--label">Group size</label>
              <vs-input class="w-full" type="number" min="2" max="20" v-model="settings.groupSize" />
              <p class="text-sm mt-1" style="color:#999">How many people make a full group (between 2 and 20, including the person who starts it).</p>
            </div>
          </div>

          <div class="flex items-center mb-4">
            <vs-switch v-model="settings.groupRequireJoin" class="mr-3" />
            <span>
              Everyone must join before the reward is sent
              <span class="text-sm" style="color:#999">
                ({{ settings.groupRequireJoin ? 'the group reward is only sent once the group is full' : 'the reward is sent right away — the group link is just for sharing' }})
              </span>
            </span>
          </div>

          <div class="flex items-center mb-6">
            <vs-switch v-model="settings.rewardEveryFriend" class="mr-3" />
            <span>
              Thank the referrer for every completed group
              <span class="text-sm" style="color:#999">
                ({{ settings.rewardEveryFriend ? 'a new thank-you each time one of their groups completes' : 'once-off: only their first completed group earns them a thank-you' }})
              </span>
            </span>
          </div>
        </template>

        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2 mb-4">
            <label class="vs-input--label" v-if="rewardMode === 'group'">Group reward</label>
            <label class="vs-input--label" v-else>Friend's welcome reward <span style="color:#aaa">(optional)</span></label>
            <vs-input class="w-full" v-model="settings.friendReward"
              :placeholder="rewardMode === 'group' ? 'e.g. A free round of coffees for the whole group' : 'e.g. Free coffee on your first visit'" />
          </div>
          <div class="vx-col w-full md:w-1/2 mb-4" v-if="rewardMode !== 'noreward'">
            <label class="vs-input--label" v-if="rewardMode === 'group'">Referrer's thank-you reward <span style="color:#aaa">(optional — handy for influencers or partners)</span></label>
            <label class="vs-input--label" v-else>Referrer's thank-you reward <span style="color:#aaa">(optional)</span></label>
            <vs-input class="w-full" v-model="settings.referrerReward" placeholder="e.g. 10% off your next visit" />
          </div>
        </div>

        <p class="mb-4 text-sm" style="color:#888" v-if="!rewardsConfigured">
          No rewards set — that's fine. Your program runs as simple lead capture:
          friends who sign up land in your referral list as leads, without welcome
          codes or reward emails.
        </p>

        <p class="mb-4 text-sm" style="color:#b58900" v-if="settings.enabled && !rewardsConfigured && !hasWebAddress">
          Tip: you haven't added your website yet, so invite emails will send friends to your
          Feedback Link page instead. Add your website under
          <router-link :to="{ name: 'settings-businessinformation' }">Business information</router-link>
          to send them there.
        </p>

        <vs-button :disabled="savingSettings" @click="saveSettings">
          <span v-if="savingSettings" class="btn-spin mr-2"></span>{{ savingSettings ? 'Saving…' : 'Save settings' }}
        </vs-button>
      </div>
    </vx-card>
  </div>
</template>

<script>
import referralsMixin from './referralsMixin.js'

export default {
  mixins: [referralsMixin],
  computed: {
    rewardsConfigured () {
      const friend = (this.settings.friendReward || '').trim() !== ''
      const referrer = this.rewardMode !== 'noreward' && (this.settings.referrerReward || '').trim() !== ''
      return friend || referrer
    },
    hasWebAddress () {
      //same resolution order the friend-invite email uses (sendReferralEmail.js):
      //businessInfo.webAddress, then publicFeedbackDetail.entityInfo.webAddress
      const biz = this.$store.state.dash.viewData.business || {}
      const businessInfo = biz.businessInfo || {}
      const entityInfo = (biz.publicFeedbackDetail && biz.publicFeedbackDetail.entityInfo) || {}
      return (businessInfo.webAddress || entityInfo.webAddress || '').trim() !== ''
    }
  },
  data () {
    return {
      //which radio option is selected; the field-write contract lives in saveSettings()
      rewardMode: 'onceoff', // 'noreward' | 'onceoff' | 'everyfriend' | 'group'
      settings: {
        enabled: false,
        mode: 'standard',
        twoSided: true,
        rewardEveryFriend: false,
        referrerReward: '',
        friendReward: '',
        groupSize: 4,
        groupRequireJoin: true
      },
      savingSettings: false,
      loadingSettings: false
    }
  },
  methods: {
    loadSettings () {
      this.loadingSettings = true
      this.$store.dispatch('dash/FETCH_REFERRALS').then((res) => {
        this.loadingSettings = false
        if (res && res.referralSettings) {
          this.settings = {
            enabled: !!res.referralSettings.enabled,
            mode: res.referralSettings.mode === 'group' ? 'group' : 'standard',
            twoSided: typeof res.referralSettings.twoSided === 'undefined' ? true : !!res.referralSettings.twoSided,
            rewardEveryFriend: typeof res.referralSettings.rewardEveryFriend === 'undefined' ? false : !!res.referralSettings.rewardEveryFriend,
            referrerReward: res.referralSettings.referrerReward || '',
            friendReward: res.referralSettings.friendReward || '',
            groupSize: this.clampGroupSize(res.referralSettings.groupSize),
            groupRequireJoin: typeof res.referralSettings.groupRequireJoin === 'undefined' ? true : !!res.referralSettings.groupRequireJoin
          }
          //derive the radio losslessly from the stored fields — exact inverse of saveSettings()
          if (this.settings.mode === 'group') this.rewardMode = 'group'
          else if (!this.settings.twoSided) this.rewardMode = 'noreward'
          else if (this.settings.rewardEveryFriend) this.rewardMode = 'everyfriend'
          else this.rewardMode = 'onceoff'
        }
      }).catch((e) => {
        this.loadingSettings = false
        this.notifyError(this.referralErrorText(e, 'Could not load your referral settings'))
      })
    },
    clampGroupSize (raw) {
      let n = parseInt(raw, 10)
      if (isNaN(n)) n = 4
      if (n < 2) n = 2
      if (n > 20) n = 20
      return n
    },
    saveSettings () {
      //field-write contract per radio option (the server re-derives/forces these too):
      // noreward    → mode 'standard', twoSided false, rewardEveryFriend false, referrer reward cleared
      // onceoff     → mode 'standard', twoSided true,  rewardEveryFriend false
      // everyfriend → mode 'standard', twoSided true,  rewardEveryFriend true
      // group       → mode 'group',    twoSided true,  rewardEveryFriend = the per-group toggle
      const m = this.rewardMode
      const payload = {
        enabled: !!this.settings.enabled,
        mode: m === 'group' ? 'group' : 'standard',
        twoSided: m !== 'noreward',
        rewardEveryFriend: m === 'group' ? !!this.settings.rewardEveryFriend : m === 'everyfriend',
        referrerReward: m === 'noreward' ? '' : (this.settings.referrerReward || ''),
        friendReward: this.settings.friendReward || '',
        groupSize: this.clampGroupSize(this.settings.groupSize),
        groupRequireJoin: !!this.settings.groupRequireJoin
      }
      this.savingSettings = true
      this.$store.dispatch('dash/UPDATE_REFERRAL_SETTINGS', { settings: payload }).then(() => {
        this.savingSettings = false
        this.settings = Object.assign({}, this.settings, payload)
        this.notifySuccess('Settings saved')
      }).catch((e) => {
        this.savingSettings = false
        this.notifyError(this.referralErrorText(e, 'Could not save your referral settings'))
      })
    }
  },
  created () {
    this.loadSettings()
  }
}
</script>

<style scoped>
.reward-mode-option {
  margin-bottom: 8px;
}
.referrals-settings .vs-input--label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  color: #666;
}
.btn-spin {
  display: inline-block;
  width: 0.9em;
  height: 0.9em;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
}
</style>
